import styled, { css } from "styled-components";

const DESKTOP = "@media (min-width: 768px)";

const glassCardBorder = css`
	&::after {
		content: "";
		position: absolute;
		inset: 0;
		border-radius: inherit;
		border: 1px solid #ffffff;
		mix-blend-mode: overlay;
		pointer-events: none;
		z-index: 2;
	}
`;

const winCardGlow = css`
	&::before {
		content: "";
		position: absolute;
		inset: 0;
		border-radius: inherit;
		background: radial-gradient(
				circle 20px at 5% 10%,
				rgba(82, 151, 255, 0.34) 0%,
				transparent 100%
			),
			radial-gradient(circle 24px at 95% 12%, rgba(82, 151, 255, 0.26) 0%, transparent 100%),
			radial-gradient(circle 18px at 6% 90%, rgba(82, 151, 255, 0.3) 0%, transparent 100%);
		pointer-events: none;
		z-index: 0;
	}
`;

export const WRAPPER = styled.section`
	display: flex;
	flex-direction: column;
	gap: ${({ $hideHeader }) => ($hideHeader ? "0" : "16px")};
	width: 100%;
	padding-top: ${({ $hideHeader }) => ($hideHeader ? "12px" : "0")};
	padding-bottom: ${({ $hideHeader }) => ($hideHeader ? "12px" : "0")};
	margin-bottom: ${({ $hideHeader }) => ($hideHeader ? "0" : "24px")};

	@media (max-width: 767px) {
		gap: ${({ $hideHeader }) => ($hideHeader ? "0" : "12px")};
		padding-top: ${({ $hideHeader }) => ($hideHeader ? "16px" : "0")};
		margin-bottom: ${({ $hideHeader }) => ($hideHeader ? "0" : "14px")};
	}
`;

export const HEADER = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	width: 100%;
`;

export const TitleBox = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	color: #e5f0fe;
	font-size: 16px;
	font-weight: 400;
	line-height: 1.1;

	img,
	svg {
		flex-shrink: 0;
		width: 16px;
		height: 16px;
		display: block;
		object-fit: contain;
	}

	${DESKTOP} {
		font-size: 16px;
		line-height: 110%;

		img,
		svg {
			width: 24px;
			height: 24px;
		}
	}
`;

export const Controls = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
`;

export const ArrowIcon = styled.img`
	width: 30px;
	height: 30px;
	display: block;
	transition: filter 0.2s ease;
	transform: ${({ $mirrored }) => ($mirrored ? "rotate(180deg)" : "none")};
`;

export const ArrowButton = styled.button`
	display: none;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	padding: 0;
	cursor: pointer;
	border: none;
	flex-shrink: 0;
	background: transparent;

	${DESKTOP} {
		display: flex;
	}

	&:hover ${ArrowIcon} {
		filter: brightness(1.25) drop-shadow(0 0 3px rgba(229, 240, 254, 0.65));
	}
`;

export const Viewport = styled.div`
	overflow: hidden;
	width: 100%;
	contain: layout style;

	${({ $touchScroll }) =>
		$touchScroll &&
		css`
			overflow-x: auto;
			overflow-y: hidden;
			contain: none;
			-webkit-overflow-scrolling: touch;
			scrollbar-width: none;
			overscroll-behavior-x: contain;
			touch-action: pan-x;

			&::-webkit-scrollbar {
				display: none;
			}
		`}
`;

export const Track = styled.div`
	display: flex;
	gap: ${({ $gap }) => `${$gap}px`};
	align-items: stretch;
	transform: ${({ $offset }) =>
		$offset !== undefined ? `translate3d(-${$offset}px,0,0)` : "none"};
	transition: ${({ $smooth }) =>
		$smooth ? "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)" : "none"};
	backface-visibility: hidden;
`;

export const WinCard = styled.button`
	position: relative;
	flex: 0 0 244px;
	width: 244px;
	min-width: 244px;
	display: block;
	min-height: 106px;
	height: 106px;
	padding: 8px 12px 8px 8px;
	border: none;
	border-radius: 14px;
	background: #171c2a;
	overflow: hidden;
	cursor: pointer;
	text-align: left;
	isolation: isolate;
	box-sizing: border-box;
	${winCardGlow}
	${glassCardBorder}
`;

export const CardInner = styled.div`
	position: relative;
	z-index: 1;
	display: flex;
	align-items: stretch;
	gap: 10px;
	width: 100%;
	height: 100%;
	min-width: 0;
`;

export const ThumbWrap = styled.div`
	position: relative;
	flex: 0 0 90px;
	width: 90px;
	height: 90px;
	align-self: center;
	overflow: hidden;
	border-radius: 12px;
	background: #171c2a;
	isolation: isolate;
	${glassCardBorder}

	img {
		position: relative;
		z-index: 1;
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
`;

export const CardBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	align-items: flex-start;
	justify-content: space-between;
	flex: 1 1 auto;
	min-width: 0;
	height: 100%;
	padding: 2px 0;
	box-sizing: border-box;
`;

export const GameName = styled.span`
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 1;
	width: 100%;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	font-size: 14px;
	font-weight: 600;
	line-height: 1.2;
	color: #e5f0fe;
	word-break: break-word;
`;

export const CardMeta = styled.span`
	display: flex;
	align-items: center;
	gap: 4px;
	width: 100%;
	min-width: 0;
	overflow: hidden;
`;

export const WinnerText = styled.span`
	flex: 0 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 11px;
	font-weight: 500;
	line-height: 1.2;
	color: #5297ff;
`;

export const MetaDot = styled.span`
	flex-shrink: 0;
	font-size: 10px;
	line-height: 1;
	color: #5f6b7d;
`;

export const DateText = styled.span`
	flex: 0 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	font-size: 10px;
	font-weight: 400;
	color: #a5b1c1;
	white-space: nowrap;
`;

export const AmountPill = styled.span`
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: flex-start;
	gap: 6px;
	width: 100%;
	max-width: 118px;
	height: 32px;
	padding: 0 10px 0 8px;
	border-radius: 16px;
	background: #202534;
	overflow: hidden;
	isolation: isolate;
	box-sizing: border-box;
	${glassCardBorder}

	&::before {
		content: "";
		position: absolute;
		inset: 0;
		border-radius: inherit;
		background: radial-gradient(
			circle 15px at 2.5px 17.5px,
			rgba(253, 216, 53, 0.35) 0%,
			transparent 100%
		);
		pointer-events: none;
		z-index: 0;
	}

	> img {
		position: relative;
		z-index: 1;
		width: 16px;
		height: 16px;
		flex-shrink: 0;
	}
`;

export const AmountText = styled.span`
	position: relative;
	z-index: 1;
	font-size: 14px;
	font-weight: 400;
	color: #e5f0fe;
	white-space: nowrap;
`;

export const AmountDecimals = styled.span`
	font-size: 14px;
	font-weight: 400;
	color: #8d99a9;
`;
